import { Address, Cell, beginCell, toNano } from '@ton/core';
import { API_BASE } from './config';

/**
 * Sand — the fast any-token lottery living on its own contract (owner is the
 * Pyramida contract). The round window length is an init parameter of each
 * deployed instance; this app reads it from the contract's getter, so the
 * same UI serves 10-minute, hourly or any other table speed.
 */
export const SAND_CONTRACT_ADDRESS: string =
  (import.meta.env?.VITE_SAND_CONTRACT_ADDRESS as string | undefined) ||
  'EQA4XLLKeJ3DdrildiQJZ66qa9OZ9b--RkIFrLZbvPyLEO2x';

export const SAND_PRMD_MASTER: string =
  (import.meta.env?.VITE_PRMD_JETTON_MASTER as string | undefined) ||
  'EQA5WvRHM55HEvyNU3vLIbjrBz0CMGxIgNVov1aZJbqNDN8a';

/** GRAM ticker icon — served from our own origin (sitev2/public/gram.svg). */
export const SAND_GRAM_ICON = '/gram.svg';

/** PRMD icon — the pyramid in a black circle, served from our own origin. */
export const SAND_PRMD_ICON = '/prmd-wallet-logo-v2.webp';

/** Icons for our own tokens come from our origin, never an external CDN. */
function localIcon(master: string, image: string): string {
  return master === SAND_PRMD_MASTER ? SAND_PRMD_ICON : image;
}

const DEFAULT_ROUND_SECONDS = 3600;

// ── backend access ────────────────────────────────────────────────────────────
// Every chain read goes through our backend (backend/src/sand-state.ts and
// backend/src/chain-reads.ts) — the browser never talks to a blockchain
// provider directly. When the backend is unreachable the reads degrade to
// empty/cached data; they never fall back to direct provider requests.

async function api<T>(path: string): Promise<T> {
  const r = await fetch(`${API_BASE}${path}`);
  if (!r.ok) throw new Error(`${path} → ${r.status}`);
  return (await r.json()) as T;
}

// ── Backend Sand snapshot ────────────────────────────────────────────────────
// The backend's stats role polls the Sand contract itself (see
// backend/src/sand-state.ts) and serves one shared snapshot.

interface BackendSandState {
  roundSeconds: number;
  rounds: {
    wallet: string;
    isTon: boolean;
    endTime: number;
    betCount: number;
    totalPool: string;
    carry?: string;
  }[];
  jettons: {
    wallet: string;
    master: string;
    symbol: string;
    decimals: number;
    image: string;
  }[];
}

let backendStateCache: { at: number; state: BackendSandState | null } | null = null;

async function fetchBackendSandState(): Promise<BackendSandState | null> {
  if (backendStateCache && Date.now() - backendStateCache.at < 10_000) {
    return backendStateCache.state;
  }
  let state: BackendSandState | null = null;
  try {
    const body = await api<{ updatedAt?: number; state?: BackendSandState | null }>(
      '/api/sand/state',
    );
    state = body.state ?? null;
  } catch {
    // backend unavailable — keep whatever we had; never read the chain directly
    if (backendStateCache) return backendStateCache.state;
  }
  backendStateCache = { at: Date.now(), state };
  return state;
}

export interface SandRound {
  wallet: string;
  isTon: boolean;
  endTime: number;
  betCount: number;
  totalPool: bigint;
  /** Prize-only part of totalPool that carries no winner-scan weight. */
  carry: bigint;
}

/** Every open round, from the backend snapshot. */
export async function fetchSandRounds(): Promise<SandRound[]> {
  const backend = await fetchBackendSandState();
  if (!backend) return [];
  return backend.rounds.map((r) => ({
    wallet: r.wallet,
    isTon: r.isTon,
    endTime: r.endTime,
    betCount: r.betCount,
    totalPool: BigInt(r.totalPool),
    carry: BigInt(r.carry ?? '0'),
  }));
}

let roundSecondsCache: number | null = null;
/** Round window length in seconds, fixed at deploy time per Sand instance. */
export async function fetchRoundSeconds(): Promise<number> {
  if (roundSecondsCache) return roundSecondsCache;
  const backend = await fetchBackendSandState();
  if (backend && backend.roundSeconds > 0) {
    roundSecondsCache = backend.roundSeconds;
    return roundSecondsCache;
  }
  return DEFAULT_ROUND_SECONDS;
}

// ── Jetton metadata ──────────────────────────────────────────────────────────

export interface SandJettonMeta {
  master: string;
  symbol: string;
  decimals: number;
  image: string;
}

// Token metadata changes rarely — cache for 5 minutes.
let sandJettonsCache: { at: number; map: Map<string, SandJettonMeta> } = {
  at: 0,
  map: new Map(),
};

/** Metadata of every jetton the Sand contract holds, keyed by Sand's jetton wallet. */
export async function fetchSandJettons(force = false): Promise<Map<string, SandJettonMeta>> {
  if (!force && Date.now() - sandJettonsCache.at < 300_000) return sandJettonsCache.map;
  const backend = await fetchBackendSandState();
  if (backend && backend.jettons.length) {
    const map = new Map<string, SandJettonMeta>();
    for (const bj of backend.jettons) {
      map.set(bj.wallet, {
        master: bj.master,
        symbol: bj.symbol,
        decimals: bj.decimals,
        image: localIcon(bj.master, bj.image),
      });
    }
    sandJettonsCache = { at: Date.now(), map };
    return map;
  }
  return sandJettonsCache.map;
}

let prmdMetaCache: { symbol: string; decimals: number; image: string } | null = null;
export async function fetchPrmdMeta() {
  if (prmdMetaCache) return prmdMetaCache;
  try {
    const j = await api<{ symbol: string; decimals: number; image: string }>(
      `/api/jetton/${encodeURIComponent(SAND_PRMD_MASTER)}`,
    );
    prmdMetaCache = {
      symbol: j.symbol === '???' ? 'PRMD' : j.symbol,
      decimals: j.decimals,
      image: SAND_PRMD_ICON,
    };
  } catch {
    prmdMetaCache = { symbol: 'PRMD', decimals: 9, image: SAND_PRMD_ICON };
  }
  return prmdMetaCache;
}

// ── Wallet holdings ──────────────────────────────────────────────────────────

export interface MyJetton {
  wallet: string;
  balance: bigint;
  symbol: string;
  decimals: number;
  image: string;
  /** Current DEX price in USD; 0 = no market price (scam/worthless token). */
  priceUsd: number;
}

export interface WalletHoldings {
  tonBalance: bigint;
  jettons: Map<string, MyJetton>;
}

let holdingsCache: { at: number; addr: string; value: WalletHoldings } | null = null;

/** The connected wallet's GRAM balance and jetton holdings (1-minute cache). */
export async function fetchWalletHoldings(
  address: string | null,
  force = false,
): Promise<WalletHoldings> {
  if (!address) return { tonBalance: 0n, jettons: new Map() };
  const me = Address.parse(address).toString();
  if (!force && holdingsCache && holdingsCache.addr === me
      && Date.now() - holdingsCache.at < 60_000) {
    return holdingsCache.value;
  }
  const jettons = new Map<string, MyJetton>();
  let tonBalance = 0n;
  try {
    const j = await api<{
      balanceNano: string;
      jettons: {
        master: string;
        wallet: string;
        balance: string;
        symbol: string;
        decimals: number;
        image: string;
        priceUsd: number;
      }[];
    }>(`/api/wallet/holdings/${encodeURIComponent(me)}`);
    tonBalance = BigInt(j.balanceNano);
    for (const b of j.jettons) {
      jettons.set(b.master, {
        wallet: b.wallet,
        balance: BigInt(b.balance),
        symbol: b.symbol,
        decimals: b.decimals,
        image: localIcon(b.master, b.image),
        priceUsd: b.priceUsd,
      });
    }
    holdingsCache = { at: Date.now(), addr: me, value: { tonBalance, jettons } };
  } catch {
    // auxiliary list — a failed read simply hides wallet-only coins
  }
  return { tonBalance, jettons };
}

/** Resolves the sender's jetton wallet for a master (used when not cached). */
export async function fetchMyJettonWallet(owner: string, master: string) {
  const j = await api<{ balance: string; walletAddress: string | null; decimals: number }>(
    `/api/wallet/jetton/${encodeURIComponent(Address.parse(owner).toString())}/${encodeURIComponent(master)}`,
  );
  if (!j.walletAddress) return null;
  return { wallet: j.walletAddress, decimals: j.decimals };
}

// ── Referral network ─────────────────────────────────────────────────────────

/*
 * The Sand contract accepts the inviter's numeric invite key on a bettor's
 * first bet — in a SandBetRef body on a GRAM bet or in the jetton transfer's
 * forward_payload — exactly like a Pyramida buy comment. The key resolves to
 * a wallet on-chain (KeyPointer), so no backend lookup is needed and the
 * set-once link survives any off-chain outage. null = no key: the contract
 * defaults the link to the owner (key 0).
 */

export interface SandRefClaim {
  /** Round key: the Sand contract's jetton wallet, or the Sand address for GRAM. */
  token: string;
  isTon: boolean;
  amount: bigint;
}

/**
 * The connected wallet's pending referral claims, walked one slot per getter
 * call. Empty when the wallet has none — or when the deployed Sand instance
 * predates the referral getters (the getter call then just fails).
 *
 * TODO(new UI): confirmed display bug to carry over — a wallet with three
 * on-chain claim slots (GRAM + HMSTR + PRMD, verified via getRefClaimSlot
 * directly) rendered only the GRAM and HMSTR rows; the PRMD row was really
 * missing, not clipped. Likely the slot's `token` (the SAND contract's
 * jetton wallet) fails to map to display metadata somewhere downstream and
 * the row is dropped instead of falling back to a raw amount. Make sure the
 * rebuilt referral-rewards UI renders EVERY slot returned here, even when
 * token metadata is unresolved, and shows dust amounts (e.g. 0.00425 GRAM)
 * instead of rounding them to a bare "0".
 */
export async function fetchSandRefClaims(address: string): Promise<SandRefClaim[]> {
  try {
    const me = Address.parse(address).toString();
    const j = await api<{ claims: { token: string; isTon: boolean; amount: string }[] }>(
      `/api/sand/ref-claims/${encodeURIComponent(me)}`,
    );
    return j.claims.map((c) => ({ token: c.token, isTon: c.isTon, amount: BigInt(c.amount) }));
  } catch {
    // old contract or transient backend failure — no claims shown
    return [];
  }
}

/** Minimum TON to attach to a ClaimRef (mirrors SAND_REF_CLAIM_GAS). */
export const SAND_CLAIM_GAS_TON = '0.1';

/** Extra TON per pending token: each cascade step is one jetton transfer. */
export const SAND_CLAIM_GAS_PER_TOKEN_TON = '0.05';

/**
 * Claim gas sized for the whole pending list: 0.05 TON per token, floored at
 * the contract-required 0.1 minimum. The cascade sends one jetton transfer
 * per step out of the attached budget and refunds the change, so a short
 * budget only pays the first few tokens — the rest stay claimable.
 */
export function claimRefGas(tokenCount: number): bigint {
  const perToken = toNano(SAND_CLAIM_GAS_PER_TOKEN_TON) * BigInt(Math.max(1, tokenCount));
  const min = toNano(SAND_CLAIM_GAS_TON);
  return perToken > min ? perToken : min;
}

/**
 * A referral claim: `ClaimRef{queryId, token, refKey?}` with the claim gas
 * attached — sized by `tokenCount` so the excess loop can pay every pending
 * token. The contract zeroes the balance and sends the tokens (GRAM
 * directly, jettons as a TEP-74 transfer) back to the caller. An optional
 * invite key registers the claimant's own link if it is not set yet — same
 * set-once rules as a bet. Key 0 (the owner) is a valid key.
 */
export function buildClaimRef(token: string, refKey?: number | null, tokenCount = 1) {
  const hasKey = refKey !== null && refKey !== undefined;
  const builder = beginCell()
    .storeUint(0x5a4d0014, 32)
    .storeUint(BigInt(Date.now()), 64)
    .storeAddress(Address.parse(token))
    .storeBit(hasKey); // maybe-bit of the optional uint64 key
  if (hasKey) builder.storeUint(BigInt(refKey), 64);
  const body = builder.endCell();
  return {
    validUntil: Math.floor(Date.now() / 1000) + 300,
    messages: [
      {
        address: SAND_CONTRACT_ADDRESS,
        amount: claimRefGas(tokenCount).toString(),
        payload: body.toBoc().toString('base64'),
      },
    ],
  };
}

// ── Bet transactions ─────────────────────────────────────────────────────────

/** SandBetRef body: the 32-bit opcode followed by the inviter's numeric key. */
function betRefCell(refKey: number): Cell {
  return beginCell().storeUint(0x5a4d0015, 32).storeUint(BigInt(refKey), 64).endCell();
}

/**
 * A GRAM bet is a plain transfer of the bet amount to the Sand contract; the
 * inviter's numeric invite key rides along in a SandBetRef body. Key 0 (the
 * owner) is valid, so only null/undefined means "no referrer".
 */
export function buildGramBet(amountStr: string, refKey?: number | null) {
  const hasKey = refKey !== null && refKey !== undefined;
  return {
    validUntil: Math.floor(Date.now() / 1000) + 300,
    messages: [
      {
        address: SAND_CONTRACT_ADDRESS,
        amount: toNano(amountStr).toString(),
        ...(hasKey ? { payload: betRefCell(refKey).toBoc().toString('base64') } : {}),
      },
    ],
  };
}

/** Parses a decimal string into jetton base units without float rounding. */
export function parseJettonAmount(amountStr: string, decimals: number): bigint {
  const [ip = '0', fp = ''] = amountStr.split('.');
  return (
    BigInt(ip) * 10n ** BigInt(decimals) +
    BigInt((fp + '0'.repeat(decimals)).slice(0, decimals) || '0')
  );
}

/**
 * A jetton bet is a TEP-74 transfer from the user's jetton wallet to the Sand
 * contract with enough forward TON for the burn + round bookkeeping.
 */
export function buildJettonBet(
  jettonWallet: string,
  owner: string,
  amount: bigint,
  refKey?: number | null,
) {
  const hasKey = refKey !== null && refKey !== undefined;
  const body = beginCell()
    .storeUint(0x0f8a7ea5, 32)
    .storeUint(BigInt(Date.now()), 64)
    .storeCoins(amount)
    .storeAddress(Address.parse(SAND_CONTRACT_ADDRESS))
    .storeAddress(Address.parse(owner))
    .storeMaybeRef(null)
    .storeCoins(toNano('0.25'))
    // forward_payload (either-bit): a SandBetRef cell carrying the key.
    .storeMaybeRef(hasKey ? betRefCell(refKey) : null)
    .endCell();
  return {
    validUntil: Math.floor(Date.now() / 1000) + 300,
    messages: [
      {
        address: jettonWallet,
        amount: toNano('0.3').toString(),
        payload: body.toBoc().toString('base64'),
      },
    ],
  };
}

// ── Table ordering by wallet liquidity ───────────────────────────────────────

export interface SandTokenValue {
  usd: number;
  priced: boolean;
}

let tokenValuesCache: { at: number; map: Map<string, SandTokenValue> } | null = null;

/**
 * Aggregate USD value of each jetton across the top players' wallets, computed
 * by the backend from periodic wallet snapshots at DEX prices. Empty tables
 * are sorted by it — the more money players hold in a token, the more of them
 * can back its table. Returns an empty map when the backend is unavailable
 * (ordering then falls back to alphabetical; nothing is hidden by this data).
 */
export async function fetchSandTokenValues(): Promise<Map<string, SandTokenValue>> {
  if (tokenValuesCache && Date.now() - tokenValuesCache.at < 300_000) {
    return tokenValuesCache.map;
  }
  const map = new Map<string, SandTokenValue>();
  if (!API_BASE) return map;
  try {
    const res = await fetch(`${API_BASE}/api/sand/token-values`);
    if (!res.ok) return map;
    const body = (await res.json()) as {
      tokens?: { master: string; usd: number; priced: boolean }[];
    };
    for (const t of body.tokens || []) {
      map.set(t.master, { usd: t.usd, priced: t.priced });
    }
    tokenValuesCache = { at: Date.now(), map };
  } catch {
    // ordering data is auxiliary — sorting falls back to alphabetical
  }
  return map;
}

// ── The wallet's own bet history with round outcomes ─────────────────────────

export interface SandBetHistoryItem {
  txHash: string; // hex — ready for the explorer link
  tokenMaster: string | null; // null = GRAM table
  amountRaw: string;
  utime: number;
  status: 'pending' | 'won' | 'lost' | 'refund';
  prizeRaw: string | null;
  drawTxHash: string | null;
}

/** The wallet's recent Sand bets with their outcomes, newest first. */
export async function fetchMySandBets(address: string): Promise<SandBetHistoryItem[]> {
  if (!API_BASE) return [];
  const r = await api<{ bets?: SandBetHistoryItem[] }>(
    `/api/sand/bets/${encodeURIComponent(address)}`,
  );
  return Array.isArray(r.bets) ? r.bets : [];
}

// ── Live token prices for pool-size ordering ─────────────────────────────────

let pricesCache: { at: number; key: string; map: Map<string, number> } | null = null;

/**
 * Current USD price per whole token for the given jetton masters (plus native
 * TON under the 'ton' key), fetched from our backend in one batched call.
 * Running tables are ordered by their pool's USD value. Missing/unpriced
 * tokens map to 0; failures return an empty map (ordering degrades gracefully).
 */
export async function fetchTokenPrices(masters: string[]): Promise<Map<string, number>> {
  const tokens = ['ton', ...masters];
  const key = tokens.join(',');
  if (pricesCache && pricesCache.key === key && Date.now() - pricesCache.at < 300_000) {
    return pricesCache.map;
  }
  const map = new Map<string, number>();
  try {
    const r = await api<{ rates: Record<string, number> }>(
      `/api/rates?tokens=${encodeURIComponent(key)}`,
    );
    for (const [addr, usd] of Object.entries(r.rates)) {
      map.set(addr.toLowerCase() === 'ton' ? 'ton' : Address.parse(addr).toString(), usd);
    }
    pricesCache = { at: Date.now(), key, map };
  } catch {
    // price data is auxiliary — pool ordering falls back to draw time
  }
  return map;
}
